import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useState } from "react";

const NAV = [
    { to: "/hpm", label: "MISSION" },
    { to: "/hpm-submit", label: "SUBMIT CASE" },
    { to: "/hpm-blog", label: "LEARN" },
    { to: "/hpm-cases/solved", label: "WALL OF WINS" },
    { to: "/hpm-firms", label: "FIND A FIRM" },
    { to: "/hpm-law-firms", label: "FOR FIRMS" },
    { to: "/courtroom", label: "COURTROOM", accent: true },
];

const TOOLS = [
    { to: "/hpm-expungement", label: "EXPUNGEMENT CHECKER", hint: "Clear your record" },
    { to: "/demand-letter", label: "DEMAND LETTER", hint: "Pre-litigation draft" },
    { to: "/hpm-dashboard", label: "MY CASES", hint: "Track submissions" },
];

export const Header = () => {
    const loc = useLocation();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [toolsOpen, setToolsOpen] = useState(false);
    const toolsActive = TOOLS.some((t) => loc.pathname === t.to);

    return (
        <header
            className="sticky top-0 z-40 bg-[#0a0a0a] border-b-2 border-white/20"
            data-testid="hpm-header"
        >
            <div className="max-w-[1600px] mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
                <Link
                    to="/hpm"
                    className="font-anton text-2xl sm:text-3xl uppercase tracking-tight flex items-center gap-2"
                    data-testid="hpm-logo"
                >
                    <span className="redact">HPM</span>
                    <span className="hidden sm:inline text-white/70 text-xs font-mono tracking-[0.2em]">
                        / HIGHER POWER MOVEMENT
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-1">
                    {NAV.map((item) => {
                        const active = loc.pathname === item.to;
                        return (
                            <Link
                                key={item.to}
                                to={item.to}
                                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 border-2 ${
                                    active
                                        ? "bg-white text-black border-white"
                                        : item.accent
                                          ? "border-[#FFCC00] text-[#FFCC00] hover:bg-[#FFCC00] hover:text-black"
                                          : "border-transparent text-white/80 hover:text-white hover:border-white/40"
                                }`}
                            >
                                {item.label}
                            </Link>
                        );
                    })}

                    <div
                        className="relative"
                        onMouseEnter={() => setToolsOpen(true)}
                        onMouseLeave={() => setToolsOpen(false)}
                    >
                        <button
                            onClick={() => setToolsOpen((v) => !v)}
                            data-testid="nav-tools-toggle"
                            className={`font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 border-2 flex items-center gap-1 ${
                                toolsActive
                                    ? "bg-white text-black border-white"
                                    : "border-transparent text-white/80 hover:text-white hover:border-white/40"
                            }`}
                        >
                            FREE TOOLS <ChevronDown size={12} />
                        </button>
                        {toolsOpen && (
                            <div
                                className="absolute top-full right-0 mt-1 w-72 bg-[#0a0a0a] border-2 border-white/30 shadow-2xl"
                                data-testid="nav-tools-dropdown"
                            >
                                {TOOLS.map((t) => {
                                    const active = loc.pathname === t.to;
                                    return (
                                        <Link
                                            key={t.to}
                                            to={t.to}
                                            onClick={() => setToolsOpen(false)}
                                            data-testid={`nav-tool-${t.to.replace(/\//g, "")}`}
                                            className={`block px-4 py-3 border-b border-white/10 last:border-0 hover:bg-white/5 ${active ? "bg-white/10" : ""}`}
                                        >
                                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                                                {t.label}
                                            </div>
                                            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/50 mt-1">
                                                {t.hint}
                                            </div>
                                        </Link>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <button
                        onClick={() => nav("/hpm-submit")}
                        className="btn-brutal btn-brutal-red ml-3"
                        data-testid="header-cta-submit"
                    >
                        Level it <span aria-hidden>→</span>
                    </button>
                </nav>

                <button
                    onClick={() => setOpen((v) => !v)}
                    className="md:hidden p-2 border-2 border-white/30"
                    data-testid="hpm-menu-toggle"
                    aria-label="menu"
                >
                    {open ? <X size={18} /> : <Menu size={18} />}
                </button>
            </div>

            {open && (
                <div
                    className="md:hidden border-t-2 border-white/20 bg-[#0a0a0a]"
                    data-testid="hpm-mobile-menu"
                >
                    {NAV.map((item) => (
                        <Link
                            key={item.to}
                            to={item.to}
                            onClick={() => setOpen(false)}
                            className="block px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] border-b border-white/10"
                        >
                            {item.label}
                        </Link>
                    ))}
                    <div className="px-6 py-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] border-b border-white/10 bg-white/5">
                        FREE TOOLS
                    </div>
                    {TOOLS.map((t) => (
                        <Link
                            key={t.to}
                            to={t.to}
                            onClick={() => setOpen(false)}
                            data-testid={`nav-mobile-tool-${t.to.replace(/\//g, "")}`}
                            className="block px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] border-b border-white/10"
                        >
                            {t.label}
                        </Link>
                    ))}
                </div>
            )}
        </header>
    );
};

export default Header;
